import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useDesignStore from '../../store/designStore';
import useProjectStore from '../../store/projectStore';
import useAuthStore from '../../store/authStore';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';

const initialForm = {
  project_id: '',
  construction_type: 'simple',
  // Tank
  working_volume_liters: '',
  freeboard_percent: '15',
  hd_ratio: '1.5',
  design_pressure_bar: '',
  design_temperature_c: '150',
  material: 'SA-240 316L',
  allowable_stress_mpa: '',
  joint_efficiency: '0.85',
  corrosion_allowance_mm: '1.5',
  full_vacuum_rating: false,
  external_design_pressure_bar: '1.0',
  elastic_modulus_mpa: '',
  poisson_ratio: '0.3',
  selected_shell_thickness_mm: '',
  selected_head_thickness_mm: '',
  // Jacket/limpet
  mode: 'heating',
  shell_coverage_percent: '50',
  bottom_head_coverage_percent: '50',
  batch_volume_liters: '',
  batch_fluid_density_kgm3: '997',
  batch_fluid_specific_heat_kj_kgk: '4.186',
  batch_initial_temp_c: '',
  batch_target_temp_c: '',
  steam_pressure_barg: '4',
  chilled_water_supply_temp_c: '7',
  chilled_water_return_temp_c: '12',
  overall_heat_transfer_coefficient_w_m2k: '',
  jacket_design_pressure_bar: '',
  annular_gap_mm: '50',
  selected_jacket_wall_thickness_mm: '',
  half_pipe_outside_diameter_mm: '88.9',
  half_pipe_wall_thickness_mm: '5.49',
  coil_pitch_mm: '150',
  assume_stiffening_effect: false,
  ring_spacing_mm: '',
  steam_nozzle_velocity_m_s: '30',
  chilled_water_nozzle_velocity_m_s: '2.5',
};

const TankDesignCalculatorPage = () => {
  const [form, setForm] = useState(initialForm);
  const [showDetails, setShowDetails] = useState(true);
  const { projects, fetchProjects } = useProjectStore();
  const { user } = useAuthStore();
  const { result, isCalculating, isSubmitting, error, calculate, saveCalculation, clearResult } = useDesignStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  const isSimple = form.construction_type === 'simple';
  const isJacketed = form.construction_type === 'jacketed';
  const isLimpeted = form.construction_type === 'limpeted';

  useEffect(() => {
    fetchProjects();
    clearResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) showToast(error, 'error');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const buildInputs = () => {
    if (!form.working_volume_liters || !form.design_pressure_bar) {
      showToast('Working volume and design pressure are required', 'error');
      return null;
    }
    if (!isSimple && (!form.batch_initial_temp_c || !form.batch_target_temp_c || !form.jacket_design_pressure_bar)) {
      showToast('Batch initial/target temperature and jacket design pressure are required for a jacketed/limpeted tank', 'error');
      return null;
    }
    return {
      construction_type: form.construction_type,
      working_volume_liters: Number(form.working_volume_liters),
      freeboard_percent: Number(form.freeboard_percent || 15),
      hd_ratio: Number(form.hd_ratio || 1.5),
      design_pressure_bar: Number(form.design_pressure_bar),
      design_temperature_c: Number(form.design_temperature_c || 150),
      material: form.material,
      allowable_stress_mpa: form.allowable_stress_mpa === '' ? null : Number(form.allowable_stress_mpa),
      joint_efficiency: Number(form.joint_efficiency),
      corrosion_allowance_mm: Number(form.corrosion_allowance_mm),
      full_vacuum_rating: form.full_vacuum_rating,
      external_design_pressure_bar: Number(form.external_design_pressure_bar || 1.0),
      elastic_modulus_mpa: form.elastic_modulus_mpa === '' ? null : Number(form.elastic_modulus_mpa),
      poisson_ratio: Number(form.poisson_ratio || 0.3),
      selected_shell_thickness_mm: form.selected_shell_thickness_mm === '' ? null : Number(form.selected_shell_thickness_mm),
      selected_head_thickness_mm: form.selected_head_thickness_mm === '' ? null : Number(form.selected_head_thickness_mm),
      mode: isSimple ? null : form.mode,
      shell_coverage_percent: Number(form.shell_coverage_percent || 50),
      bottom_head_coverage_percent: Number(form.bottom_head_coverage_percent || 50),
      batch_volume_liters: form.batch_volume_liters === '' ? null : Number(form.batch_volume_liters),
      batch_fluid_density_kgm3: Number(form.batch_fluid_density_kgm3),
      batch_fluid_specific_heat_kj_kgk: Number(form.batch_fluid_specific_heat_kj_kgk),
      batch_initial_temp_c: isSimple || form.batch_initial_temp_c === '' ? null : Number(form.batch_initial_temp_c),
      batch_target_temp_c: isSimple || form.batch_target_temp_c === '' ? null : Number(form.batch_target_temp_c),
      steam_pressure_barg: Number(form.steam_pressure_barg || 4),
      chilled_water_supply_temp_c: Number(form.chilled_water_supply_temp_c || 7),
      chilled_water_return_temp_c: Number(form.chilled_water_return_temp_c || 12),
      overall_heat_transfer_coefficient_w_m2k: form.overall_heat_transfer_coefficient_w_m2k === '' ? null : Number(form.overall_heat_transfer_coefficient_w_m2k),
      jacket_design_pressure_bar: isSimple || form.jacket_design_pressure_bar === '' ? null : Number(form.jacket_design_pressure_bar),
      annular_gap_mm: Number(form.annular_gap_mm || 50),
      selected_jacket_wall_thickness_mm: form.selected_jacket_wall_thickness_mm === '' ? null : Number(form.selected_jacket_wall_thickness_mm),
      half_pipe_outside_diameter_mm: Number(form.half_pipe_outside_diameter_mm || 88.9),
      half_pipe_wall_thickness_mm: Number(form.half_pipe_wall_thickness_mm || 5.49),
      coil_pitch_mm: Number(form.coil_pitch_mm || 150),
      assume_stiffening_effect: form.assume_stiffening_effect,
      ring_spacing_mm: form.ring_spacing_mm === '' ? null : Number(form.ring_spacing_mm),
      steam_nozzle_velocity_m_s: Number(form.steam_nozzle_velocity_m_s || 30),
      chilled_water_nozzle_velocity_m_s: Number(form.chilled_water_nozzle_velocity_m_s || 2.5),
    };
  };

  const handleCalculate = async (e) => {
    e.preventDefault();
    const inputs = buildInputs();
    if (!inputs) return;
    await calculate('tank_design', inputs);
  };

  const handleSave = async () => {
    if (!result) return;
    const payload = {
      calculator_type: 'tank_design',
      project_id: form.project_id ? Number(form.project_id) : null,
      created_by_id: user?.id || null,
      client_data: result.inputs,
      calculation_outputs: result.outputs,
    };
    const saveResult = await saveCalculation(payload);
    if (saveResult.success) {
      showToast('Calculation saved as draft', 'success');
      navigate('/design/calculations');
    } else {
      showToast(saveResult.error, 'error');
    }
  };

  const tank = result?.outputs?.tank;
  const jacket = result?.outputs?.jacket;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-xl font-bold text-white mb-1">Tank Design</h1>
        <p className="text-xs text-gray-500 mb-6">
          Unified tank sizing (ASME VIII) + optional jacket/limpet coil design in one flow.
          Same formulas/caveats as the standalone Tank Sizing and Jacket/Limpet Coil calculators —
          see "Assumptions" below the result. Requires review by a qualified pressure vessel
          engineer before fabrication.
        </p>

        <div className="glass-card p-6">
          <form onSubmit={handleCalculate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project (optional)</label>
              <select
                value={form.project_id}
                onChange={handleChange('project_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Not linked to a project</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Construction Type *</label>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2">
                  <input type="radio" checked={isSimple} onChange={() => setForm((f) => ({ ...f, construction_type: 'simple' }))} />
                  Simple
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" checked={isJacketed} onChange={() => setForm((f) => ({ ...f, construction_type: 'jacketed' }))} />
                  Jacketed (annular)
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" checked={isLimpeted} onChange={() => setForm((f) => ({ ...f, construction_type: 'limpeted' }))} />
                  Limpeted (half-pipe coil)
                </label>
              </div>
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-2">Tank</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Working Volume (L) *</label>
                <input required type="number" step="any" value={form.working_volume_liters} onChange={handleChange('working_volume_liters')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Freeboard (%)</label>
                <input type="number" step="any" value={form.freeboard_percent} onChange={handleChange('freeboard_percent')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Standard: 15" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">H/D Ratio</label>
                <input type="number" step="any" value={form.hd_ratio} onChange={handleChange('hd_ratio')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Typical: 1.0–1.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Pressure (bar) *</label>
                <input required type="number" step="any" value={form.design_pressure_bar} onChange={handleChange('design_pressure_bar')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Design Temperature (°C)</label>
                <input type="number" step="any" value={form.design_temperature_c} onChange={handleChange('design_temperature_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material</label>
                <input type="text" value={form.material} onChange={handleChange('material')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Allowable Stress, S (MPa)</label>
                <input type="number" step="any" value={form.allowable_stress_mpa} onChange={handleChange('allowable_stress_mpa')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = typical default" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Joint Efficiency, E</label>
                <input type="number" step="any" max="1" value={form.joint_efficiency} onChange={handleChange('joint_efficiency')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Corrosion Allowance (mm)</label>
                <input type="number" step="any" value={form.corrosion_allowance_mm} onChange={handleChange('corrosion_allowance_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <p className="text-xs font-medium text-gray-700 mb-2">
                Selected Plate Thickness <span className="text-gray-400 font-normal">(optional — checked against the calculated requirement, PASS/FAIL, for the ASME report)</span>
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Selected Shell Thickness (mm)</label>
                  <input type="number" step="any" value={form.selected_shell_thickness_mm} onChange={handleChange('selected_shell_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = no adequacy check" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Selected Head Thickness (mm)</label>
                  <input type="number" step="any" value={form.selected_head_thickness_mm} onChange={handleChange('selected_head_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = no adequacy check" />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                <input type="checkbox" checked={form.full_vacuum_rating} onChange={(e) => setForm((f) => ({ ...f, full_vacuum_rating: e.target.checked }))} />
                Also check full vacuum / external pressure rating
              </label>
              {form.full_vacuum_rating && (
                <div className="grid grid-cols-3 gap-4 mt-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">External Pressure Differential (bar)</label>
                    <input type="number" step="any" value={form.external_design_pressure_bar} onChange={handleChange('external_design_pressure_bar')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Full vacuum = 1.0" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Elastic Modulus, E (MPa)</label>
                    <input type="number" step="any" value={form.elastic_modulus_mpa} onChange={handleChange('elastic_modulus_mpa')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = SS default" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Poisson's Ratio</label>
                    <input type="number" step="any" value={form.poisson_ratio} onChange={handleChange('poisson_ratio')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                </div>
              )}
            </div>

            {!isSimple && (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-2">
                  {isJacketed ? 'Jacket' : 'Limpet Coil'}
                </h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Duty *</label>
                  <div className="flex gap-4 text-sm">
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={form.mode === 'heating'} onChange={() => setForm((f) => ({ ...f, mode: 'heating' }))} />
                      Heating (steam)
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" checked={form.mode === 'cooling'} onChange={() => setForm((f) => ({ ...f, mode: 'cooling' }))} />
                      Cooling (chilled water)
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch Volume (L)</label>
                    <input type="number" step="any" value={form.batch_volume_liters} onChange={handleChange('batch_volume_liters')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = tank working volume" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Initial Temp (°C) *</label>
                    <input required type="number" step="any" value={form.batch_initial_temp_c} onChange={handleChange('batch_initial_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Temp (°C) *</label>
                    <input required type="number" step="any" value={form.batch_target_temp_c} onChange={handleChange('batch_target_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                  </div>
                </div>

                {form.mode === 'heating' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Steam Supply Pressure (barg)</label>
                      <input type="number" step="any" value={form.steam_pressure_barg} onChange={handleChange('steam_pressure_barg')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Steam Nozzle Velocity (m/s)</label>
                      <input type="number" step="any" value={form.steam_nozzle_velocity_m_s} onChange={handleChange('steam_nozzle_velocity_m_s')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 30" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Supply (°C)</label>
                      <input type="number" step="any" value={form.chilled_water_supply_temp_c} onChange={handleChange('chilled_water_supply_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Chilled Water Return (°C)</label>
                      <input type="number" step="any" value={form.chilled_water_return_temp_c} onChange={handleChange('chilled_water_return_temp_c')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nozzle Velocity (m/s)</label>
                      <input type="number" step="any" value={form.chilled_water_nozzle_velocity_m_s} onChange={handleChange('chilled_water_nozzle_velocity_m_s')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 2.5" />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shell Coverage (%)</label>
                    <input type="number" step="any" value={form.shell_coverage_percent} onChange={handleChange('shell_coverage_percent')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bottom Dish Coverage (%)</label>
                    <input type="number" step="any" value={form.bottom_head_coverage_percent} onChange={handleChange('bottom_head_coverage_percent')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: 50" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Overall U (W/m²K)</label>
                    <input type="number" step="any" value={form.overall_heat_transfer_coefficient_w_m2k} onChange={handleChange('overall_heat_transfer_coefficient_w_m2k')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = typical default" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jacket Design Pressure (bar) *</label>
                  <input required type="number" step="any" value={form.jacket_design_pressure_bar} onChange={handleChange('jacket_design_pressure_bar')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" />
                </div>

                {isJacketed ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Annular Gap (mm)</label>
                      <input type="number" step="any" value={form.annular_gap_mm} onChange={handleChange('annular_gap_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Selected Jacket Wall Thickness (mm)</label>
                      <input type="number" step="any" value={form.selected_jacket_wall_thickness_mm} onChange={handleChange('selected_jacket_wall_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = no adequacy check" />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Half-Pipe OD (mm)</label>
                      <input type="number" step="any" value={form.half_pipe_outside_diameter_mm} onChange={handleChange('half_pipe_outside_diameter_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Default: NPS 3 Sch 40" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Half-Pipe Wall Thickness (mm)</label>
                      <input type="number" step="any" value={form.half_pipe_wall_thickness_mm} onChange={handleChange('half_pipe_wall_thickness_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Coil Pitch (mm)</label>
                      <input type="number" step="any" value={form.coil_pitch_mm} onChange={handleChange('coil_pitch_mm')} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
                    <input type="checkbox" checked={form.assume_stiffening_effect} onChange={(e) => setForm((f) => ({ ...f, assume_stiffening_effect: e.target.checked }))} />
                    Show indicative stiffening-ring credit (jacket pressure vs. inner wall)
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    Unvalidated directional estimate only — shown alongside, never replacing, the conservative requirement.
                  </p>
                  {form.assume_stiffening_effect && isJacketed && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Assumed Stiffening Ring Spacing (mm)</label>
                      <input type="number" step="any" value={form.ring_spacing_mm} onChange={handleChange('ring_spacing_mm')} className="w-48 border border-gray-300 rounded px-3 py-2 text-sm" placeholder="Blank = 300mm default" />
                    </div>
                  )}
                  {form.assume_stiffening_effect && isLimpeted && (
                    <p className="text-xs text-gray-500">Uses the Coil Pitch entered above as the stiffener spacing.</p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button type="submit" disabled={isCalculating} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
                {isCalculating ? 'Calculating...' : 'Calculate'}
              </button>
            </div>
          </form>
        </div>

        {result && tank && (
          <div className="glass-card p-6 mt-4">
            <h2 className="font-semibold text-gray-900 mb-4">Result</h2>

            {result.outputs.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4 space-y-1">
                {result.outputs.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-800">⚠ {w}</p>
                ))}
              </div>
            )}

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Tank Geometry</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Inside Diameter" value={`${tank.inside_diameter_mm} mm`} highlight />
              <Stat label="Straight Shell Height" value={`${tank.straight_shell_height_mm} mm`} />
              <Stat label="Overall Height" value={`${tank.overall_height_mm} mm`} highlight />
              <Stat label="Total Volume (shell + bottom dish)" value={`${tank.actual_total_volume_liters} L`} />
              <Stat label="Working Volume" value={`${tank.actual_working_volume_liters} L`} />
              <Stat label="Physical Volume (incl. top dish)" value={`${tank.actual_physical_volume_liters} L`} />
            </div>

            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Tank Thickness</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
              <Stat label="Allowable Stress (S)" value={`${tank.allowable_stress_mpa} MPa${tank.s_value_is_default ? ' (default)' : ''}`} />
              <Stat label="Shell Governing Case" value={tank.shell_governing_case} />
              <Stat label="Shell Thickness (calculated)" value={`${tank.shell_thickness_required_mm} mm`} />
              <Stat label="Shell Recommended Plate" value={`${tank.shell_recommended_plate_mm} mm`} highlight />
              <Stat label="Head Thickness (calculated)" value={`${tank.head_thickness_required_mm} mm`} />
              <Stat label="Head Recommended Plate" value={`${tank.head_recommended_plate_mm} mm`} highlight />
              {tank.shell_thickness_pass != null && (
                <Stat
                  label={`Shell Adequacy (${tank.selected_shell_thickness_mm}mm selected)`}
                  value={tank.shell_thickness_pass ? `PASS (+${tank.shell_thickness_margin_percent}%)` : `FAIL (${tank.shell_thickness_margin_percent}%)`}
                  highlight={tank.shell_thickness_pass}
                  danger={!tank.shell_thickness_pass}
                />
              )}
              {tank.head_thickness_pass != null && (
                <Stat
                  label={`Head Adequacy (${tank.selected_head_thickness_mm}mm selected)`}
                  value={tank.head_thickness_pass ? `PASS (+${tank.head_thickness_margin_percent}%)` : `FAIL (${tank.head_thickness_margin_percent}%)`}
                  highlight={tank.head_thickness_pass}
                  danger={!tank.head_thickness_pass}
                />
              )}
            </div>

            {jacket && (
              <>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Heat Transfer</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
                  <Stat label="Total Jacket Area" value={`${jacket.total_jacket_area_m2} m²`} />
                  <Stat label="Covered Shell Height" value={`${jacket.covered_shell_height_mm} mm`} />
                  <Stat label="Overall U" value={`${jacket.overall_u_w_m2k} W/m²K${jacket.u_value_is_default ? ' (default)' : ''}`} />
                  <Stat label="Batch Time" value={`${jacket.time_hours} hr`} highlight />
                  <Stat label="Initial (Peak) Duty" value={`${jacket.duty_initial_kw} kW`} />
                  <Stat label="Final Duty" value={`${jacket.duty_final_kw} kW`} />
                  {jacket.utility_type === 'steam' ? (
                    <Stat label="Steam Required" value={`${jacket.utility_flow_kg_hr} kg/hr`} highlight />
                  ) : (
                    <Stat label="Chilled Water Required" value={`${jacket.utility_flow_m3_hr} m³/hr`} highlight />
                  )}
                  <Stat label="Utility Nozzle" value={`${jacket.utility_nozzle_diameter_mm} mm`} highlight />
                  <Stat label="Nozzle Velocity" value={`${jacket.utility_nozzle_velocity_m_s} m/s`} />
                </div>

                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Jacket Structural</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm mb-4">
                  {jacket.jacket_wall_thickness_required_mm != null ? (
                    <>
                      <Stat label="Jacket Wall (calculated)" value={`${jacket.jacket_wall_thickness_required_mm} mm`} />
                      <Stat label="Jacket Recommended Plate" value={`${jacket.jacket_wall_recommended_plate_mm} mm`} highlight />
                      {jacket.jacket_wall_pass != null && (
                        <Stat
                          label={`Jacket Wall Adequacy (${jacket.selected_jacket_wall_thickness_mm}mm selected)`}
                          value={jacket.jacket_wall_pass ? `PASS (+${jacket.jacket_wall_margin_percent}%)` : `FAIL (${jacket.jacket_wall_margin_percent}%)`}
                          highlight={jacket.jacket_wall_pass}
                          danger={!jacket.jacket_wall_pass}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      <Stat label="Half-Pipe Wall (calculated)" value={`${jacket.half_pipe_wall_thickness_required_mm} mm`} />
                      <Stat label="Given Wall Adequate?" value={jacket.half_pipe_given_wall_adequate ? 'Yes' : 'No — increase schedule'} highlight />
                      <Stat label="Coil Length" value={`${jacket.coil_length_m} m (${jacket.coil_number_of_turns} turns)`} highlight />
                    </>
                  )}
                  {jacket.inner_wall_check && (
                    <Stat
                      label="Inner Wall vs Jacket Pressure"
                      value={jacket.inner_wall_check.inner_wall_given_adequate ? 'Adequate' : `Insufficient (need ${jacket.inner_wall_check.inner_wall_thickness_required_mm}mm)`}
                      highlight={!jacket.inner_wall_check.inner_wall_given_adequate}
                    />
                  )}
                  {jacket.inner_wall_check?.stiffening_credit && (
                    <Stat
                      label="Indicative Stiffened Thickness"
                      value={`~${jacket.inner_wall_check.stiffening_credit.indicative_stiffened_thickness_required_mm} mm (unverified)`}
                    />
                  )}
                </div>
              </>
            )}

            {(result.outputs.calculation_steps || result.outputs.assumptions) && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <button type="button" onClick={() => setShowDetails((v) => !v)} className="text-xs font-semibold text-komal-blue hover:underline mb-3">
                  {showDetails ? '− Hide calculation & assumptions' : '+ Show calculation & assumptions'}
                </button>

                {showDetails && (
                  <div className="space-y-4">
                    {result.outputs.calculation_steps?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Calculation Steps</h3>
                        <div className="bg-gray-50 rounded border border-gray-100">
                          <ScrollX>
                          <table className="w-full text-xs">
                            <tbody>
                              {result.outputs.calculation_steps.map((step, i) => (
                                <tr key={i} className={step.formula === '' && step.result === '' ? 'bg-gray-100' : 'border-b border-gray-100 last:border-0'}>
                                  <td className={`px-3 py-2 whitespace-nowrap ${step.formula === '' && step.result === '' ? 'font-bold text-gray-500' : 'font-medium text-gray-700'}`} colSpan={step.formula === '' && step.result === '' ? 3 : 1}>{step.step}</td>
                                  {!(step.formula === '' && step.result === '') && (
                                    <>
                                      <td className="px-3 py-2 text-gray-500 font-mono whitespace-nowrap">{step.formula}</td>
                                      <td className="px-3 py-2 text-gray-900 font-mono">{step.result}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </ScrollX>
                        </div>
                      </div>
                    )}

                    {result.outputs.assumptions?.length > 0 && (
                      <div>
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Assumptions</h3>
                        <ul className="list-disc list-inside space-y-1">
                          {result.outputs.assumptions.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600">{a}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 text-right mt-4">
              Save this calculation to generate a formal ASME design report (letterhead, watermark, full calculation
              working, and thickness adequacy sign-off) from its detail page.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => clearResult()} className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                Discard
              </button>
              <button type="button" onClick={handleSave} disabled={isSubmitting} className="px-4 py-2 text-sm rounded bg-green-600 hover:bg-green-700 text-white font-semibold disabled:opacity-60">
                {isSubmitting ? 'Saving...' : 'Save as Draft'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight, danger }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`font-semibold ${danger ? 'text-red-600 text-lg' : highlight ? 'text-komal-blue text-lg' : 'text-gray-900'}`}>{value}</div>
  </div>
);

export default TankDesignCalculatorPage;
